"use client";

import { useTranslations } from "next-intl";
import { useQueryStates } from "nuqs";
import { useRef, useState } from "react";

import { CheckBox } from "@/common/components/form/checkbox";
import { useOnClickOutside } from "@/common/hooks/use-click-outside";
import { ArrowsUpDown, FiltersIcon } from "@/common/icons";
import { blogsQueryParsers } from "@/features/blog/helpers/query-cache";

const filterValues = [
  "Business Resources",
  "General",
  "Marketing",
  "Print Reseller",
  "Publishing",
  "eCommerce",
];

export function FiltersDropdown() {
  const t = useTranslations("common");

  const [{ page, limit, categories }, setQueryStates] = useQueryStates(blogsQueryParsers);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useOnClickOutside(dropdownRef, () => setIsOpen(false));

  const onChange = async (value: string) => {
    const newSelectedValues = [...categories];
    if (newSelectedValues.includes(value)) {
      newSelectedValues.splice(newSelectedValues.indexOf(value), 1);
    } else {
      newSelectedValues.push(value);
    }
    const newState = { page, limit, categories: newSelectedValues };
    await setQueryStates(newState, { history: "replace", shallow: false, throttleMs: 300 });
  };

  const onClearAll = async () => {
    const newState = { page, limit, categories: [] };
    await setQueryStates(newState, { history: "replace", shallow: false, throttleMs: 300 });
  };

  return (
    <div className="relative flex flex-col items-center justify-center">
      <button
        onClick={() => setIsOpen(true)}
        className="border-grey-20 flex h-[40px] w-[180px] place-content-between items-center justify-between rounded border p-3"
        aria-haspopup="true"
        aria-expanded={isOpen}
        role="combobox"
        aria-controls="select-dropdown"
        tabIndex={0}
      >
        <div className="flex items-center gap-3">
          <FiltersIcon />
          <span className="text-grey-90 text-sm font-bold">{t("text-filters")}</span>
        </div>
        <ArrowsUpDown />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="border-grey-20 absolute left-0 top-[72px] z-10 flex w-[180px] flex-col gap-6 rounded border bg-white p-4"
          role="menu"
        >
          {categories.length > 0 && (
            <button className="text-grey-80 self-end text-xs underline" onClick={onClearAll}>
              {t("text-clear-all")}
            </button>
          )}
          {filterValues.map((filterItem) => (
            <label key={filterItem} className="flex cursor-pointer flex-row" role="menuitem">
              <CheckBox
                checked={categories.includes(filterItem)}
                onChange={() => onChange(filterItem)}
              />
              <span className="text-nowrap text-sm">{filterItem}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
